import { BRAND, brandUrl } from './brand'
import { siteCopy } from '../content/siteCopy'

const marketingBase = brandUrl('marketing').replace(/\/$/, '')

export function absoluteAssetUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${marketingBase}${normalized}`
}

export function organizationJsonLd() {
  return {
    '@type': 'Organization',
    '@id': `${marketingBase}/#organization`,
    name: BRAND.brandName,
    url: marketingBase,
    logo: {
      '@type': 'ImageObject',
      url: absoluteAssetUrl(BRAND.logos.color),
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: BRAND.email.support,
      url: absoluteAssetUrl('/contact'),
    },
  }
}

export function websiteJsonLd() {
  return {
    '@type': 'WebSite',
    '@id': `${marketingBase}/#website`,
    url: marketingBase,
    name: BRAND.appName,
    description: siteCopy.promo,
    publisher: { '@id': `${marketingBase}/#organization` },
    inLanguage: 'en',
  }
}

function monthlyPlanOffer() {
  const monthlyPlan = siteCopy.pricing.plans.find((plan) => plan.id === 'monthly')
  if (!monthlyPlan) return undefined

  const priceMatch = monthlyPlan.price.match(/[\d.]+/)
  if (!priceMatch) return undefined

  return {
    '@type': 'Offer',
    price: priceMatch[0],
    priceCurrency: 'GBP',
    description: monthlyPlan.description,
  }
}

export function mobileApplicationJsonLd(ogImageUrl) {
  return {
    '@type': 'MobileApplication',
    '@id': `${marketingBase}/#app`,
    name: BRAND.appName,
    alternateName: `${BRAND.appName} ${BRAND.tagline}`,
    operatingSystem: 'iOS, Android',
    applicationCategory: 'HealthApplication',
    description: siteCopy.promo,
    image: ogImageUrl,
    url: marketingBase,
    publisher: { '@id': `${marketingBase}/#organization` },
    offers: monthlyPlanOffer(),
  }
}

export function webPageJsonLd({ title, description }, canonicalUrl) {
  return {
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: title,
    description,
    isPartOf: { '@id': `${marketingBase}/#website` },
    about: { '@id': `${marketingBase}/#app` },
    inLanguage: 'en',
  }
}

export function breadcrumbJsonLd(items, canonicalUrl) {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteAssetUrl(item.path),
    })),
  }
}

export function faqPageJsonLd(items, canonicalUrl) {
  return {
    '@type': 'FAQPage',
    '@id': `${canonicalUrl}#faq`,
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

export function faqItemsForPath(pathname) {
  if (pathname === '/') {
    return siteCopy.pricing.faq
  }
  if (pathname === '/aboutus') {
    return siteCopy.aboutUs.faq.items
  }
  return null
}

export function pageStructuredData(pathname, seo, canonicalUrl, ogImageUrl) {
  const graph = [
    organizationJsonLd(),
    websiteJsonLd(),
    mobileApplicationJsonLd(ogImageUrl),
    webPageJsonLd(seo, canonicalUrl),
  ]

  if (seo.breadcrumb?.length > 1) {
    graph.push(breadcrumbJsonLd(seo.breadcrumb, canonicalUrl))
  }

  const faqItems = faqItemsForPath(pathname)
  if (faqItems?.length) {
    graph.push(faqPageJsonLd(faqItems, canonicalUrl))
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  }
}
